import React from 'react';
import { css } from '@emotion/css';
import MUITab from '@mui/material/Tab';
import MUITabs from '@mui/material/Tabs';
import cn from 'classnames';

import { TAB_APPEARANCE, TAB_VARIANT } from './types';

const min_height_class = css`
  min-height: unset;
`;

export type CONFIG_TYPE = {
  tab_classes: React.ComponentProps<typeof MUITab>['classes'];
  tabs_classes: React.ComponentProps<typeof MUITabs>['classes'];
};

const black_typography = css`
  &.Mui-selected {
    color: var(--interactive-primary);
  }
`;
const white_typography = css`
  &.Mui-selected {
    color: var(--interactive-white);
  }
`;

const tab_indicator = css`
  @media (forced-colors: active) {
    background-color: CanvasText;
  }
`;

export const indicator_animation = css`
  @keyframes ly_indicator {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }

  animation: ly_indicator 150ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const DEFAULT_TABS_CLASSES: CONFIG_TYPE['tabs_classes'] = {
  flexContainer: 'space-s-32',
  indicator: cn('bg-interactive-active transition-none', tab_indicator),
  root: cn(min_height_class, black_typography),
};
const DEFAULT_WHITE_TABS_CLASSES: CONFIG_TYPE['tabs_classes'] = {
  ...DEFAULT_TABS_CLASSES,
  indicator: cn('bg-interactive-white transition-none', tab_indicator),
  root: cn(min_height_class, white_typography),
};

const tab_typography = cn(
  'min-w-0 p-0 opacity-100 normal-case text-text-placeholder',
  min_height_class,
);

export const CONFIG: { [key in TAB_VARIANT]: CONFIG_TYPE } = {
  caps: {
    tab_classes: {
      root: cn(tab_typography, black_typography, 'mb-18 label-caps'),
    },
    tabs_classes: DEFAULT_TABS_CLASSES,
  },
  large: {
    tab_classes: {
      root: cn(tab_typography, black_typography, 'heading-xs'),
    },
    tabs_classes: DEFAULT_TABS_CLASSES,
  },
  small: {
    tab_classes: {
      root: cn(tab_typography, black_typography, 'heading-xxs'),
    },
    tabs_classes: DEFAULT_TABS_CLASSES,
  },
};

export const NEW_CONFIG: {
  [key in TAB_VARIANT]: { [appearance in TAB_APPEARANCE]: CONFIG_TYPE };
} = {
  caps: {
    inverse: {
      tab_classes: {
        root: cn(tab_typography, white_typography, 'mb-18 label-caps hover:text-text-white'),
      },
      tabs_classes: DEFAULT_WHITE_TABS_CLASSES,
    },
    primary: {
      tab_classes: {
        root: cn(tab_typography, black_typography, 'mb-18 label-caps hover:text-text-primary'),
      },
      tabs_classes: DEFAULT_TABS_CLASSES,
    },
  },
  large: {
    inverse: {
      tab_classes: {
        root: cn(tab_typography, white_typography, 'mb-8 heading-xs hover:text-text-white'),
      },
      tabs_classes: DEFAULT_WHITE_TABS_CLASSES,
    },
    primary: {
      tab_classes: {
        root: cn(tab_typography, black_typography, 'mb-8 heading-xs hover:text-text-primary'),
      },
      tabs_classes: DEFAULT_TABS_CLASSES,
    },
  },
  small: {
    inverse: {
      tab_classes: {
        root: cn(tab_typography, white_typography, 'mb-8 heading-xxs hover:text-text-white'),
      },
      tabs_classes: DEFAULT_WHITE_TABS_CLASSES,
    },
    primary: {
      tab_classes: {
        root: cn(tab_typography, black_typography, 'mb-8 heading-xxs hover:text-text-primary'),
      },
      tabs_classes: DEFAULT_TABS_CLASSES,
    },
  },
};
